import { useState } from 'react';

import type { Subscription } from '@repo/subscription/api';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';

import useFreeTrial from '../../../hooks/useFreeTrial';
import LoadingPlaceholder from '../LoadingPlaceholder/LoadingPlaceholder';
import ModalWrapper from '../ModalWrapper';

import FreeTrialOffboarding from './FreeTrialOffboarding';
import GeneralOffboarding from './GeneralOffboarding';
import { ModalContentContainer } from './styles';
import { OffboardingModalProps } from './types';

const OffboardingModal: React.FC<OffboardingModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  modalProps,
  ...contentProps
}) => {
  const { isRunning: isFreeTrialRunning, isCancelled: isFreeTrialCancelled } =
    useFreeTrial();
  const [isLoading, setIsLoading] = useState(false);

  const [newSubscription, setNewSubscription] = useState<Subscription | null>(
    null
  );
  const updateSubscription = useSubscriptionStore(
    subscriptionStoreSelector.updateSubscription
  );

  const handleSubscriptionUpdate = (subscription: Subscription): void => {
    (onSuccess || updateSubscription)(subscription);
  };

  const handleClose = (): void => {
    if (isLoading) {
      return;
    }

    if (newSubscription) {
      handleSubscriptionUpdate(newSubscription);
    }
    onClose();
  };

  return (
    <ModalWrapper
      height="unset"
      isOpen={isOpen}
      width="540px"
      onClose={handleClose}
      {...modalProps}
    >
      <ModalContentContainer>
        {isLoading && <LoadingPlaceholder backgroundStyle="blurred" />}
        {isFreeTrialRunning && !isFreeTrialCancelled ? (
          <FreeTrialOffboarding
            {...contentProps}
            onClose={handleClose}
            onFinishLoading={(): void => setIsLoading(false)}
            onStartLoading={(): void => setIsLoading(true)}
            onSuccess={setNewSubscription}
          />
        ) : (
          <GeneralOffboarding
            {...contentProps}
            onClose={handleClose}
            onFinishLoading={(): void => setIsLoading(false)}
            onStartLoading={(): void => setIsLoading(true)}
            onSuccess={handleSubscriptionUpdate}
          />
        )}
      </ModalContentContainer>
    </ModalWrapper>
  );
};

export default OffboardingModal;
